// Builds the ordered list of playable questions from a question set. Walks bulks
// in `order`, then each bulk's `sequence`, mapping every item to its topic_slug.
// Card items (info/cheat/worked summaries) are reference-only and skipped.
// This is a single-set composition; the richer iOS SessionComposer (multi-set,
// wrong-answer pool) is a later parity item.
import type { QuestionSetItemRead, QuestionSetRead } from "@/api/library-types";

export const CARD_KINDS = new Set<string>(["info", "cheat", "worked", "recap", "solution", "reference_figure"]);

export interface PlaylistEntry {
  item: QuestionSetItemRead;
  topicSlug?: string;
  /** Originating set's framework slug — gates content features (e.g. word gloss). */
  frameworkSlug?: string | null;
}

export function buildPlaylist(set: QuestionSetRead): PlaylistEntry[] {
  const itemById = new Map(set.items.map((i) => [i.id, i]));
  const bulks = [...set.bulks].sort((a, b) => a.order - b.order);
  const out: PlaylistEntry[] = [];

  const frameworkSlug = set.source_framework_slug;
  for (const bulk of bulks) {
    for (const id of bulk.sequence) {
      const item = itemById.get(id);
      if (item && !CARD_KINDS.has(item.item_kind)) {
        out.push({ item, topicSlug: bulk.topic_slug, frameworkSlug });
      }
    }
  }

  // Fallback for sets without bulk sequences: every non-card item, set order.
  if (out.length === 0) {
    for (const item of set.items) {
      if (!CARD_KINDS.has(item.item_kind)) out.push({ item, frameworkSlug });
    }
  }

  return out;
}
